import { useStore } from '../lib/store.ts';
import { tasksStore } from '../state/tasks.ts';
import type { TabKind } from '../state/tabs.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { useVoiceCapability, VOICE_OFF_TITLE } from '../voice/capability.ts';
import { showVoiceError, VoiceError } from '../voice/errors.ts';

// Onderste navigatie op de telefoon: Mail (met de teller van vragen of resultaten), Chat, Spreek
// (oranje: de spraakmodus op Mail), Agenda en Meer (de rest van de functies in een vel). De schil
// verbergt hem zodra een mail of gesprek open staat, zodat lezer en instructiebalk de ruimte krijgen.
// Zonder ElevenLabs-sleutel is Spreek gedempt: een tik geeft één melding in plaats van een poging.

export type NavId = 'mail' | 'chat' | 'voice' | 'agenda' | 'more';

export interface NavItem {
  id: NavId;
  label: string;
  icon: IconName;
  badge?: { count: number; tone: 'blue' | 'amber' } | undefined;
}

/** Functies die onder Meer vallen; is er zo'n tabblad open, dan licht Meer op. */
const MORE_KINDS: ReadonlySet<TabKind> = new Set(['rules', 'dev', 'settings', 'knowledge', 'usage', 'briefing', 'releases', 'excel', 'whatsapp']);

export function navItems(counts: { done: number; question: number }): NavItem[] {
  const badge = counts.question > 0 ? { count: counts.question, tone: 'amber' as const } : counts.done > 0 ? { count: counts.done, tone: 'blue' as const } : undefined;
  return [
    { id: 'mail', label: 'Mail', icon: 'mail', badge },
    { id: 'chat', label: 'Chat', icon: 'chat' },
    { id: 'voice', label: 'Spreek', icon: 'mic' },
    { id: 'agenda', label: 'Agenda', icon: 'calendar' },
    { id: 'more', label: 'Meer', icon: 'menu' },
  ];
}

export function BottomNav(props: { active: TabKind | null; onSelect: (kind: 'mail' | 'chat' | 'agenda') => void; onVoice: () => void; voiceActive: boolean; onMore: () => void; moreOpen: boolean }) {
  const counts = useStore(tasksStore, (s) => s.counts);
  const voice = useVoiceCapability();
  const voiceMuted = !voice.dictation;
  const items = navItems(counts);
  const isActive = (it: NavItem) => (it.id === 'more' ? props.moreOpen || (props.active !== null && MORE_KINDS.has(props.active)) : it.id === 'voice' ? props.voiceActive : props.active === it.id);
  return (
    <nav aria-label="Functies" className="shrink-0 bg-surface border-t border-line pb-safe">
      <div className="h-[58px] flex items-stretch">
        {items.map((it) => {
          const active = isActive(it);
          if (it.id === 'voice') {
            return (
              <button
                key={it.id}
                type="button"
                aria-label="Spraakmodus"
                aria-pressed={props.voiceActive}
                aria-disabled={voiceMuted || undefined}
                title={voiceMuted ? VOICE_OFF_TITLE : undefined}
                onClick={voiceMuted ? () => showVoiceError(new VoiceError('off'), 'dictation') : props.onVoice}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-amber ${voiceMuted ? 'opacity-50' : ''}`}
              >
                <span className={`h-10 w-10 rounded-full flex items-center justify-center ${props.voiceActive ? 'bg-amber-soft text-amber ring-2 ring-amber' : 'bg-amber text-white shadow-card'}`}>
                  <Icon name="mic" size={22} />
                </span>
                {it.label}
              </button>
            );
          }
          return (
            <button
              key={it.id}
              type="button"
              aria-label={it.label}
              aria-current={active && it.id !== 'more' ? 'page' : undefined}
              aria-expanded={it.id === 'more' ? props.moreOpen : undefined}
              onClick={() => (it.id === 'more' ? props.onMore() : props.onSelect(it.id as 'mail' | 'chat' | 'agenda'))}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${active ? 'text-accent' : 'text-muted'}`}
            >
              <span className="relative">
                <Icon name={it.icon} size={24} stroke={active ? 2.2 : 1.8} />
                {it.badge ? (
                  <span className={`absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold text-white flex items-center justify-center ring-2 ring-surface ${it.badge.tone === 'amber' ? 'bg-amber' : 'bg-accent'}`} title={it.badge.tone === 'amber' ? 'Vragen aan jou' : 'Resultaten klaar'}>
                    {it.badge.count}
                  </span>
                ) : null}
              </span>
              {it.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
